<template>
    <div>
        <C1Label
            v-if="label"
            :label-for="labelFor"
        >
            <div class="afu-flex afu-justify-between afu-items-center">
                <div>
                    {{ label }}
                </div>
                <div>
                    <slot name="label-right" />
                </div>
            </div>
        </C1Label>

        <div class="afu-relative">
            <div
                v-if="$slots['icon']"
                class="af-input-prefix afu-absolute afu-flex afu-items-center afu-inset-y-0 afu-left-0 afu-pl-3 afu-text-secondary"
            >
                <div style="width: 14px;">
                    <slot name="icon" />
                </div>
            </div>

            <input
                ref="input"
                :id="id"
                :class="classList"
                :type="localInputType"
                :name="name"
                :min="min"
                :max="max"
                :value="localValue"
                :disabled="disabled"
                :readonly="readonly"
                :placeholder="placeholder"
                :autocomplete="autocomplete"
                v-on="inputListeners"
            >

            <div
                v-if="type === 'password'"
                class="afu-absolute afu-inset-y-0 afu-right-0 afu-pr-3 afu-flex afu-items-center afu-cursor-pointer"
                @click="toggleTextPassword()"
            >
                <FontAwesomeIcon
                    :icon="eyeIcon"
                    :class="classEyeIconList"
                />
            </div>

            <div
                v-if="showX && localValue"
                class="af-input-icon-x afu-absolute afu-top-0 afu-inset-y-0 afu-right-0 afu-pr-3 afu-flex afu-items-center afu-cursor-pointer afu-bg-transparent afu-text-neutral-dark hover:afu-text-neutral-dark"
                @click.stop.prevent="onClear"
            >
                <svg
                    class="afu-h-3 afu-w-3"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 8 8"
                >
                    <path
                        stroke-linecap="round"
                        stroke-width="1.5"
                        d="M1 1l6 6m0-6L1 7"
                    />
                </svg>
            </div>
        </div>

        <C1HelpText
            v-if="invalidFeedback"
            variant="danger"
        >
            {{ invalidFeedback }}
        </C1HelpText>
    </div>
</template>

<script>
    import C1HelpText from './C1HelpText';
    import C1Label from './C1Label';

    export default {
        name: 'C1FormInput',

        components: {
            C1HelpText,
            C1Label,
        },

        props: {
            value: {
                type: [String, Number],
                default: '',
            },

            id: {
                type: String,
                default: undefined,
            },

            type: {
                type: String,
                default: 'text',
                validator: value =>
                    [
                        'text',
                        'password',
                        'email',
                        'number',
                        'date',
                        'month',
                    ].includes(value),
            },

            placeholder: {
                type: String,
                default: '',
            },

            name: {
                type: String,
                default: '',
            },

            size: {
                type: String,
                default: null,
                validator: value => ['sm', 'lg', 'naked', 'xl'].includes(value),
            },

            isInvalid: {
                type: Boolean,
                default: false,
            },

            invalidFeedback: {
                type: [String, Boolean],
                default: false,
            },

            label: {
                type: String,
                default: '',
            },

            labelFor: {
                type: String,
                default: '',
            },

            min: {
                type: String,
                default: null,
            },

            max: {
                type: String,
                default: null,
            },

            showX: {
                type: Boolean,
                default: false,
            },

            disabled: {
                type: Boolean,
                default: false,
            },

            readonly: {
                type: Boolean,
                default: false,
            },

            autocomplete: {
                type: String,
                default: 'off',
            },
        },

        data() {
            return {
                localValue: this.value,
                localInputType: this.type,
            };
        },

        computed: {
            classList() {
                return [
                    this.getDefaultClass,
                    this.getTextColorClass,
                    this.getTextSizeClass,
                    this.getBorderClass,
                    this.getBgColorClass,
                    this.getPaddingClass,
                ];
            },

            getDefaultClass() {
                let output = ['afu-box-border'];
                if (this.size !== 'naked') {
                    output.push(' af-input');
                }
                if (this.size) {
                    output.push(`af-input-${this.size}`);
                }

                if (this.$slots.icon) {
                    output.push('af-input-icon');
                }

                if (this.isPassword) {
                    output.push('af-input-password');
                }

                if (this.isInvalid) {
                    output.push('af-input-invalid');
                }

                output.push(
                    'afu-block focus:afu-outline-none afu-appearance-none afu-w-full afu-transition afu-duration-150 afu-ease-in-out',
                );

                return output.join(' ');
            },

            getTextColorClass() {
                if (this.disabled) {
                    return 'afu-text-neutral-dark afu-placeholder-custom';
                }

                return 'afu-text-black afu-placeholder-custom';
            },

            getTextSizeClass() {
                const sizes = {
                    default: 'afu-text-7xl afu-font-normal',
                    sm: 'afu-text-7xl afu-font-normal',
                    lg: 'afu-text-7xl afu-font-normal',
                    naked: 'afu-text-xl md:afu-text-6xl afu-font-normal',
                };

                return sizes[this.size] || sizes.default;
            },

            getBorderClass() {
                if (this.isInvalid) {
                    return 'afu-border-danger-400 focus:afu-border-danger-500';
                }

                if (this.disabled) {
                    return 'afu-border-inputBorder-400';
                }

                if (this.size == 'naked') {
                    return 'afu-border-none';
                }

                return 'afu-border-inputBorder-400 focus:afu-border-inputBorder-600';
            },

            getBgColorClass() {
                return 'afu-bg-white';
            },

            getPaddingClass() {
                const sizes = {
                    default: () => {
                        let output = ['afu-h-10 afu-py-2'];

                        output.push(this.isPassword ? 'afu-pr-10' : 'afu-pr-3');
                        output.push(this.$slots.icon ? 'afu-pl-9' : 'afu-pl-3');

                        return output;
                    },
                    sm: () => {
                        let output = ['afu-py-2'];
                        output.push(
                            !this.isPassword ? 'afu-px-3' : 'afu-pl-3 afu-pr-10',
                        );

                        return output;
                    },
                    lg: () => {
                        let output = ['afu-py-3'];
                        output.push(
                            !this.isPassword ? 'afu-px-5' : 'afu-pl-5 afu-pr-10',
                        );

                        return output;
                    },
                    naked: () => {
                        let output = ['afu-p-0 afu-pt-1'];
                        return output;
                    },
                };

                return sizes[this.size]
                    ? sizes[this.size]()
                    : sizes.default().join(' ');
            },

            classEyeIconList() {
                return this.isInvalid ? 'afu-text-danger-400' : 'afu-text-custom';
            },

            isPassword() {
                return this.localInputType === 'password';
            },

            eyeIcon() {
                return this.isPassword ? ['far', 'eye'] : ['far', 'eye-slash'];
            },

            inputListeners() {
                const vm = this;
                return {
                    ...this.$listeners,
                    input(event) {
                        const v = event.target.value;
                        vm.localValue = v;
                        vm.$emit('input', v);
                    },
                    blur(event) {
                        vm.$emit('blur', event);
                    },
                };
            },
        },

        watch: {
            value(newVal) {
                this.localValue = newVal;
            },
        },

        methods: {
            toggleTextPassword() {
                this.localInputType = this.isPassword ? 'text' : 'password';
            },

            onClear() {
                this.$emit('clear', '');
                this.$emit('input', '');
            },
        },
    };
</script>
<style lang="scss" scoped>
    .af-input-icon-x {
        top: 50%;
        transform: translateY(-50%);
    }
</style>